import {
  AdvancedFilterDataSchema,
  FilterGroupSchema,
} from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import {
  SortDirection,
  SortDirectionSchema,
} from "@redotech/redo-views-common/table";
import * as z from "zod";
import {
  adjustmentSessionKindLabels,
  adjustmentSessionStatusLabels,
} from "./adjustment-session-labels";
import type { AdjustmentSessionSearchRow } from "./search-adjustment-sessions";

export const adjustmentSessionExportColumnKeys = [
  "reference",
  "kind",
  "status",
  "createdAt",
  "userName",
  "locationName",
  "notes",
  "productCount",
  "totalQuantity",
] as const;

export type AdjustmentSessionExportColumnKey =
  (typeof adjustmentSessionExportColumnKeys)[number];

export const AdjustmentSessionExportColumnKeySchema = z.enum(
  adjustmentSessionExportColumnKeys,
);

export const ADJUSTMENT_SESSION_CSV_HEADER_MAP: Record<
  AdjustmentSessionExportColumnKey,
  string
> = {
  reference: "Reference",
  kind: "Type",
  status: "Status",
  createdAt: "Created",
  userName: "User",
  locationName: "Location",
  notes: "Notes",
  productCount: "Products",
  totalQuantity: "Total quantity",
};

export function isAdjustmentSessionExportColumnKey(
  key: string,
): key is AdjustmentSessionExportColumnKey {
  return key in ADJUSTMENT_SESSION_CSV_HEADER_MAP;
}

function formatCreatedAt(createdAt: string, timeZoneId: string): string {
  const date = Temporal.Instant.from(createdAt)
    .toZonedDateTimeISO(timeZoneId)
    .toPlainDate();
  return date.toLocaleString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function getAdjustmentSessionCsvValue(
  row: AdjustmentSessionSearchRow,
  key: AdjustmentSessionExportColumnKey,
  timeZoneId: string,
): string {
  switch (key) {
    case "reference":
      return row.inboundShipmentNumber || row.sourceId || "-";
    case "kind":
      return adjustmentSessionKindLabels[row.kind];
    case "status":
      return adjustmentSessionStatusLabels[row.status];
    case "createdAt":
      return formatCreatedAt(row.createdAt, timeZoneId);
    case "userName":
      return row.teamMemberName || "Unknown";
    case "locationName":
      return row.locationName;
    case "notes":
      return row.notes || "-";
    case "productCount":
      return row.productCount.toString();
    case "totalQuantity":
      return row.totalQuantity.toString();
  }
}

export const exportAdjustmentSessions = {
  name: "exportAdjustmentSessions" as const,
  input: z.object({
    search: z.string().optional(),
    sort: z
      .object({ key: z.string(), direction: SortDirectionSchema })
      .optional(),
    filterGroup: FilterGroupSchema.nullish(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).default([]),
    visibleColumns: z.array(AdjustmentSessionExportColumnKeySchema),
    sendToEmail: z.string().email().optional(),
  }),
  output: z.object({
    exportId: z.string(),
    fileUrl: z.string().optional(),
    failed: z.boolean().optional(),
  }),
};

export interface ExportAdjustmentSessionsSort {
  key: string;
  direction: SortDirection;
}
